import { useFormContext, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormField } from "../FormField";
import { Asterisk } from "lucide-react";
import { ResidentFormData } from "../../schemas/residentForm";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface ResidentCompanyFormProps {
  arrayIndex: number;
  disabled?: boolean;
}

export const ResidentCompanyForm: React.FC<ResidentCompanyFormProps> = ({
  arrayIndex,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const {
    register,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<ResidentFormData>();
  const tenantErrors = errors.enterprise_tenants?.[arrayIndex];

  return (
    <div className="flex flex-col gap-4 sm:gap-6 md:gap-[24px]">
      <p className="font-bold text-off-black text-base sm:text-lg md:text-[20px]">
        {t("form.identity.title")}
      </p>

      <div className="flex flex-col gap-4 sm:gap-6 md:gap-[24px]">
        {/* Company Name and E-mail */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-[16px]">
          <FormField
            label={t("form.identity.companyName")}
            required
            error={tenantErrors?.nom_societe?.message}
            className="flex-1"
          >
            <input
              type="text"
              {...register(`enterprise_tenants.${arrayIndex}.nom_societe`)}
              placeholder={t("form.placeholders.companyName")}
              disabled={disabled}
              className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288] w-full"
            />
          </FormField>
          <FormField
            label={t("form.identity.email")}
            required
            error={tenantErrors?.email_representant?.message}
            className="flex-1"
          >
            <input
              type="email"
              {...register(`enterprise_tenants.${arrayIndex}.email_representant`)}
              placeholder={t("form.placeholders.email")}
              disabled={disabled}
              className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
            />
          </FormField>
        </div>

        {/* Civilité */}
        <FormField
          label={t("form.identity.civilite")}
          required
          error={tenantErrors?.civilite?.message}
        >
          <Controller
            name={`enterprise_tenants.${arrayIndex}.civilite`}
            control={control}
            render={({ field }) => (
              <Select
                value={field.value || ""}
                onValueChange={(value) => {
                  field.onChange(value);
                  field.onBlur();
                }}
                disabled={disabled}
              >
                <SelectTrigger
                  data-testid="civilite-select"
                  className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288] w-full"
                >
                  <SelectValue placeholder={t("form.identity.civilite")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monsieur">
                    {t("form.identity.civiliteOptions.monsieur")}
                  </SelectItem>
                  <SelectItem value="madame">
                    {t("form.identity.civiliteOptions.madame")}
                  </SelectItem>
                  <SelectItem value="ne_souhaite_pas_communiquer">
                    {t("form.identity.civiliteOptions.ne_souhaite_pas_communiquer")}
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </FormField>

        {/* Contact Person Name and First Name */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-[16px]">
          <FormField
            label={t("form.identity.lastName")}
            required
            error={tenantErrors?.nom_representant?.message}
            className="flex-1"
          >
            <input
              type="text"
              {...register(`enterprise_tenants.${arrayIndex}.nom_representant`)}
              placeholder={t("form.placeholders.lastName")}
              disabled={disabled}
              className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
            />
          </FormField>
          <FormField
            label={t("form.identity.firstName")}
            required
            error={tenantErrors?.prenom_representant?.message}
            className="flex-1"
          >
            <input
              type="text"
              {...register(`enterprise_tenants.${arrayIndex}.prenom_representant`)}
              placeholder={t("form.placeholders.firstName")}
              disabled={disabled}
              className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
            />
          </FormField>
        </div>

        {/* Téléphone bureau and Téléphone portable */}
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-[16px]">
          <FormField
            label={t("form.identity.officePhone")}
            error={tenantErrors?.tel_professionnel?.message}
            className="flex-1"
          >
            <input
              type="tel"
              {...register(`enterprise_tenants.${arrayIndex}.tel_professionnel`)}
              placeholder={t("form.placeholders.workPhone")}
              disabled={disabled}
              className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
            />
          </FormField>
          <FormField
            label={t("form.identity.mobilePhone")}
            error={tenantErrors?.tel_portable?.message}
            className="flex-1"
          >
            <input
              type="tel"
              {...register(`enterprise_tenants.${arrayIndex}.tel_portable`)}
              placeholder={t("form.placeholders.phone")}
              disabled={disabled}
              className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
            />
          </FormField>
        </div>

        {/* Correspondance par e-mail */}
        <div className="flex flex-col gap-4 sm:gap-[16px]">
          <div className="flex items-center gap-[5px]">
            <label className="font-bold text-off-black text-base">
              {t("form.identity.emailCorrespondence")}
            </label>
            <Asterisk className="w-[8px] h-[8px] text-off-red" />
          </div>
          <div className="flex flex-wrap gap-4 sm:gap-[24px]">
            <label className="flex items-center gap-[8px] cursor-pointer">
              <input
                type="radio"
                name={`enterprise_tenants.${arrayIndex}.correspondance_email`}
                checked={watch(`enterprise_tenants.${arrayIndex}.correspondance_email`) === true}
                onChange={() =>
                  setValue(`enterprise_tenants.${arrayIndex}.correspondance_email`, true, {
                    shouldValidate: true,
                  })
                }
                disabled={disabled}
              />
              <span className="text-off-black text-[16px]">{t("form.identity.yes")}</span>
            </label>
            <label className="flex items-center gap-[8px] cursor-pointer">
              <input
                type="radio"
                name={`enterprise_tenants.${arrayIndex}.correspondance_email`}
                checked={watch(`enterprise_tenants.${arrayIndex}.correspondance_email`) === false}
                onChange={() =>
                  setValue(`enterprise_tenants.${arrayIndex}.correspondance_email`, false, {
                    shouldValidate: true,
                  })
                }
                disabled={disabled}
              />
              <span className="text-off-black text-[16px]">{t("form.identity.no")}</span>
            </label>
          </div>
        </div>

        {/* Correspondence Address */}
        <div className="flex flex-col gap-4 sm:gap-[16px] w-full">
          <p className="font-bold text-off-black text-base">
            {t("form.identity.correspondenceAddress")}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-[16px]">
            <FormField
              label={t("form.identity.street")}
              error={tenantErrors?.rue?.message}
              className="flex-1"
            >
              <input
                type="text"
                {...register(`enterprise_tenants.${arrayIndex}.rue`)}
                placeholder={t("form.placeholders.street")}
                disabled={disabled}
                className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
              />
            </FormField>
            <FormField
              label={t("form.identity.number")}
              error={tenantErrors?.numero?.message}
              className="flex-1"
            >
              <input
                type="text"
                {...register(`enterprise_tenants.${arrayIndex}.numero`)}
                placeholder={t("form.placeholders.number")}
                disabled={disabled}
                className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
              />
            </FormField>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-[16px]">
            <FormField
              label={t("form.identity.postalCode")}
              error={tenantErrors?.npa?.message}
              className="flex-1"
            >
              <input
                type="number"
                {...register(`enterprise_tenants.${arrayIndex}.npa`, { valueAsNumber: true })}
                placeholder={t("form.placeholders.postalCode")}
                disabled={disabled}
                className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
              />
            </FormField>
            <FormField
              label={t("form.identity.city")}
              error={tenantErrors?.ville?.message}
              className="flex-1"
            >
              <input
                type="text"
                {...register(`enterprise_tenants.${arrayIndex}.ville`)}
                placeholder={t("form.placeholders.city")}
                disabled={disabled}
                className="h-[48px] px-[14px] py-[10px] border border-[#d0d5dd] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-primary text-base text-[#757288]"
              />
            </FormField>
          </div>
        </div>
      </div>
    </div>
  );
};
